import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useData } from '../contexts/DataContext';
import { useT, WORK_TYPES } from '../lib/i18n';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, FilePlus2, ClipboardList } from 'lucide-react';

const money = (n) => (Number(n) || 0).toFixed(2) + ' $';

export default function Templates() {
  const { templates = [], removeTemplate } = useData();
  const { lang } = useT();
  const nav = useNavigate();
  const [deletingId, setDeletingId] = useState(null);

  const LABELS = {
    fr: {
      title: 'Mes modèles',
      subtitle: 'Soumissions réutilisables — gagne du temps en pré-remplissant tes prochaines soumissions',
      empty: 'Aucun modèle pour le moment',
      emptyHelp: 'Ouvre une soumission existante et clique « Sauvegarder comme modèle ». Ou crée-en un nouveau depuis zéro.',
      createBlank: 'Créer un nouveau modèle',
      useTemplate: 'Utiliser ce modèle',
      delete: 'Supprimer',
      confirmDelete: 'Supprimer ce modèle ?',
      materials: 'matériaux',
      backHome: 'Retour',
    },
    en: {
      title: 'My templates',
      subtitle: 'Reusable quotes — save time by pre-filling your next quotes',
      empty: 'No templates yet',
      emptyHelp: 'Open an existing quote and click "Save as template". Or create a fresh one from scratch.',
      createBlank: 'Create a new template',
      useTemplate: 'Use this template',
      delete: 'Delete',
      confirmDelete: 'Delete this template?',
      materials: 'materials',
      backHome: 'Back',
    },
  };
  const labels = LABELS[lang] || LABELS.fr;

  const onUse = async (tpl) => {
    // Persist a flag for NewSubmission to pick up on mount
    try {
      window.sessionStorage.setItem('soumisspro_use_template_id', tpl.id);
    } catch { /* ignore */ }
    nav('/submissions/new');
  };

  const onDelete = async (id) => {
    if (!window.confirm(labels.confirmDelete)) return;
    setDeletingId(id);
    await removeTemplate(id);
    setDeletingId(null);
    toast.success('✓');
  };

  return (
    <Layout title={labels.title}>
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-bold text-gray-600 mb-4">
        <ArrowLeft size={16} /> {labels.backHome}
      </Link>

      <div className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tighter mb-1">{labels.title}</h1>
        <p className="text-sm text-gray-500">{labels.subtitle}</p>
      </div>

      <button
        data-testid="create-blank-template-btn"
        onClick={() => {
          try { window.sessionStorage.removeItem('soumisspro_use_template_id'); } catch { /* ignore */ }
          try { window.sessionStorage.setItem('soumisspro_save_as_template', '1'); } catch { /* ignore */ }
          nav('/submissions/new');
        }}
        className="btn-outline w-full gap-2 mb-4 border-dashed"
      >
        <Plus size={18} /> {labels.createBlank}
      </button>

      {templates.length === 0 && (
        <div data-testid="templates-empty" className="card p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#FAF7F2] border border-[#E5DCC9] mx-auto flex items-center justify-center mb-3">
            <ClipboardList className="text-[#7A1F2B]" size={24} />
          </div>
          <div className="font-heading font-black text-lg mb-1">{labels.empty}</div>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">{labels.emptyHelp}</p>
        </div>
      )}

      <div className="space-y-3">
        {templates.map((tpl) => {
          const matCount = (tpl.materials || []).length;
          const workTypeLabel = (WORK_TYPES[lang] && WORK_TYPES[lang][tpl.work_type]) || tpl.work_type || '—';
          // Rough materials total preview
          const matTotal = (tpl.materials || []).reduce(
            (acc, m) => acc + (Number(m.quantity) || 0) * (Number(m.unit_price) || 0),
            0
          );
          return (
            <div
              key={tpl.id}
              data-testid={`template-card-${tpl.id}`}
              className="card p-4"
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-[#7A1F2B] text-[#C9A961] flex items-center justify-center shrink-0">
                  <ClipboardList size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold truncate">{tpl.name}</div>
                  <div className="text-xs text-gray-500">{workTypeLabel}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {matCount} {labels.materials} · ≈ {money(matTotal)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-3">
                <button
                  data-testid={`use-template-${tpl.id}`}
                  onClick={() => onUse(tpl)}
                  className="btn-primary gap-2"
                >
                  <FilePlus2 size={16} /> {labels.useTemplate}
                </button>
                <button
                  data-testid={`delete-template-${tpl.id}`}
                  onClick={() => onDelete(tpl.id)}
                  disabled={deletingId === tpl.id}
                  className="btn-outline gap-2 !text-red-700 !border-red-300 disabled:opacity-50"
                >
                  <Trash2 size={16} /> {labels.delete}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
